import React from "react";

import FormGroup from "../../formGroup";
import FieldsWrapper from "../../inputs/field/FieldsWrapper";
import { IFieldDef } from "../FieldInfo";
import SmartField from "../smartField/SmartField";
import { getTooltip } from "../smartField/SmartFieldUtils";
import SmartCollapsedFormGroup from "../smartFormGroup/SmartCollapsedFormGroup";
import SmartFormGroupBase, { ISmartFormGroupBaseProps } from "../smartFormGroup/SmartFormGroupBase";
import SmartBulkChangeField, { ISmartBulkChangeCheckboxChange } from "./SmartBulkChangeField";

interface IProps extends ISmartFormGroupBaseProps {
    onBulkChangeCheckboxChange?: (args: ISmartBulkChangeCheckboxChange) => void;
}

export default class SmartBulkChangeFormGroup extends SmartFormGroupBase<IProps> {

    handleExpand = (): void => {
        this.forceUpdate();
    };

    renderField = (rows: IFieldDef[], _isCustom = false): React.ReactElement[] => {
        const array: React.ReactElement[] = [];

        for (let i = 0; i < rows.length; i++) {
            const path = rows[i].id;
            const field = this.props.storage.data.mergedDefinition?.[path]?.fieldDef || rows[i];
            const bc = this.props.bindingContext.navigate(field.id);
            const { hasVisibleCollapsedFields } = this.getCollapsedFieldsVisibility(field);

            array.push(
                <SmartBulkChangeField
                    key={bc.toString()}
                    bindingContext={bc}
                    storage={this.props.storage}
                    hasVisibleCollapsedFields={hasVisibleCollapsedFields}
                    fieldProps={{
                        isLight: false
                    }}
                    onConfirm={this.handleConfirm}
                    onCancel={this.handleCancel}
                    onTemporalChange={this.handleTemporalChange}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    onCheckboxChange={this.props.onBulkChangeCheckboxChange}
                />
            );
        }

        return array;
    };

    renderCollapsedPanel = (lastField: IFieldDef): React.ReactElement => {
        const { hasVisibleCollapsedFields, hasEnabledCollapsedFields } = this.getCollapsedFieldsVisibility(lastField);

        if (!hasVisibleCollapsedFields || !lastField.collapsedRows?.length) {
            return null;
        }

        return (
            <SmartCollapsedFormGroup
                key={`collapsed-${lastField.id}`}
                storage={this.props.storage}
                def={lastField}
                hasEditableFields={hasEnabledCollapsedFields}
                onExpand={this.handleExpand}>
                {lastField.collapsedRows.map((row, rowIdx) => (
                    <FieldsWrapper key={rowIdx}>
                        {row.map(fieldRef => {
                            const bc = this.props.bindingContext.navigate(fieldRef.id);
                            return (
                                <SmartField
                                    key={bc.toString()}
                                    bindingContext={bc}
                                    storage={this.props.storage}
                                    onChange={this.handleChange}
                                    onBlur={this.handleBlur}
                                />
                            );
                        })}
                    </FieldsWrapper>
                ))}
            </SmartCollapsedFormGroup>
        );
    };

    // rowsTop -- groups.rows has nested array
    renderFields = (rowsTop: (IFieldDef[])[], isCustom: boolean, _showCollapsedGroup = true): React.ReactElement[] => {
        const array: React.ReactElement[] = [];
        return (rowsTop || []).reduce((ret, rows) => {
            ret.push(...this.renderField(rows, isCustom));
            const lastField = this.getFieldDefinition(rows[rows.length - 1]);
            if (lastField?.collapsedRows && !lastField?.noCollapsedExpansion) {
                ret.push(...this.renderFields(lastField.collapsedRows, false));
            } else if (lastField?.collapsedRows && lastField?.noCollapsedExpansion) {
                const panel = this.renderCollapsedPanel(lastField);
                if (panel) {
                    ret.push(panel);
                }
            }
            return ret;
        }, array);
    };


    render() {
        if (this.props.def.lineItems) {
            // skip group with lineItems
            return null;
        }

        return (
            <FormGroup
                id={this.props.def.id}
                title={this.props.title ?? this.props.def.title as string}
                showLine
                tooltip={getTooltip(this.props.def.tooltip, this.props.storage)}>

                {this.props.def.rows &&
                <FieldsWrapper isColumn>
                    {this.renderFields(this.props.def.rows, false)}
                </FieldsWrapper>
                }
                {/*this.props.def.lineItems && this.renderLineItems(this.props.def.lineItems)*/}
            </FormGroup>
        );
    }
}
